import AthenaAPI from '@/APIs/AthenaAPI';
import {UserInfo} from '@/APIs/types';
import {ContactFormStore} from '@/Agent/ContactForm/ContactFormStore/ContactFormStore';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import {first, last, map} from 'lodash';
import moment from 'moment';
import {ProsperFormValues} from '../ProductTabs/lib/validation';
import {MIN_COVERAGE_AMOUNT, MIN_PREMIUM_AMOUNT} from '../constants';
interface premiumType {
    type: string;
    annual: number;
    modal: number;
    quarterly: number;
    semiAnnual: number;
    monthly: number;
    biweekly: number;
    bimonthly: number;
    weekly: number;
    ninthly: number;
    tenthly: number;
}
function formatDate(year: number, month: number, day: number): string {
    const date = new Date(Date.UTC(year, month - 1, day));

    const isoString = date.toISOString();

    return isoString;
}

async function fetchPolicyData(formData: UserInfo & ProsperFormValues & {applicationId: string}): Promise<any> {
    return await AthenaAPI.calcfocus(formData);
}

const generateOutList = (
    formData: ProsperFormValues,
    policySummary: any,
    parameterStore: ParameterStore,
    resp: any,
) => {
    const {premium = [], scenarioSummary = []} = policySummary;
    const [firstScenario = {}] = scenarioSummary;
    const {endOfYearValues = []} = firstScenario;
    const [firstYear = {}] = endOfYearValues;

    const getPremiumByType = (type: string) => premium.find((p: premiumType) => p.type === type);

    const targetPremium = getPremiumByType('TARGET_PREMIUM_AT_ILLUSTRATION_START');
    const levelMaxPremium = getPremiumByType('GUIDELINE_LEVEL');
    const initialAnnualPremium = Number(firstYear?.sumPremiumsPaid ?? NaN);
    const initialMonthlyPremium = Number.isFinite(initialAnnualPremium) ? initialAnnualPremium / 12 : NaN;
    const initialDeathBenefit = firstYear.faceAmount;

    const accumulatedAgeRows = map(endOfYearValues, 'surrenderCashValue');
    const premiumValues = map(endOfYearValues, 'sumOutlay');

    const cashValuesAtAges = getCashValueAtAge(parameterStore, policySummary);
    const cumulativePremiumValuesAtAges = getSumCumulativePremiumsPaidAtAge(parameterStore, policySummary);

    const valueAtAgeYAxis = cashValuesAtAges ? cashValuesAtAges.map(([_, value]) => value) : [];
    const cumulativeAtAgeYAxis = cumulativePremiumValuesAtAges
        ? cumulativePremiumValuesAtAges.map(([_, value]) => value)
        : [];

    type OutlistItem = {
        name: string;
        value: any;
        type: 'currency' | 'string' | 'year';
        fieldName?: string;
        field?: string;
        xAxis?: number[];
    };

    const outList: OutlistItem[] = [
        {name: 'Product name', value: 'Prosper', type: 'string', fieldName: 'productName'},
        {name: 'Carrier', type: 'string', value: 'EMC', field: 'carrier'},
        {
            name: 'Policy annual premium',
            type: Number.isFinite(initialAnnualPremium) ? 'currency' : 'string',
            value: Number.isFinite(initialAnnualPremium) ? initialAnnualPremium : 'Minimum premium not met',
        },
        {
            name: 'Initial monthly premium',
            value: Number.isNaN(initialMonthlyPremium) ? 'Minimum premium not met' : initialMonthlyPremium,
            type: Number.isNaN(initialMonthlyPremium) ? 'string' : 'currency',
            fieldName: 'monthlyPremium',
        },
        {
            name: 'Target annual premium',
            value: targetPremium?.annual,
            type: 'currency',
            fieldName: 'targetAnnualPremium',
        },
        ...((levelMaxPremium
            ? [
                  {
                      name: 'Level max premium',
                      value: levelMaxPremium.annual,
                      type: 'currency',
                      fieldName: 'levelMaxPremium',
                  },
              ]
            : []) as OutlistItem[]),
        {
            name: 'Initial death benefit',
            value: initialDeathBenefit,
            type: 'currency',
            fieldName: 'faceAmount',
        },
        {
            name: 'Solve Type',
            value: formData.prosperAmplifySolves,
            type: 'string',
            fieldName: 'solveType',
        },
        {name: 'Accumulated Value', type: 'year', value: accumulatedAgeRows, fieldName: 'cashValue'},
        {name: 'Annual Premium', type: 'year', value: premiumValues, fieldName: 'cashValue'},
        {
            name: 'Cash Value at Age',
            type: 'year',
            value: valueAtAgeYAxis,
            xAxis: [65, 70, 75, 80, 85],
        },
        {
            name: 'Cumulative Premiums Paid at Age',
            type: 'year',
            value: cumulativeAtAgeYAxis,
            xAxis: [65, 70, 75, 80, 85],
        },
    ];

    if (formData.prosperTableRating) {
        outList.push({
            name: 'Table Rating',
            value: formData.prosperTableRating,
            type: 'string',
            fieldName: 'prosperTableRating',
        });
    }

    if (cashValuesAtAges && cumulativePremiumValuesAtAges) {
        for (let i = 0; i < cashValuesAtAges.length; i++) {
            // Add prosper value
            outList.push({
                name: cashValuesAtAges[i][0],
                value: cashValuesAtAges[i][1],
                type: 'currency',
                fieldName: 'prosperValueAtAge',
            });

            // Add corresponding premium value
            outList.push({
                name: cumulativePremiumValuesAtAges[i][0],
                value: cumulativePremiumValuesAtAges[i][1],
                type: 'currency',
                fieldName: 'cumulativePremiumValueAtAge',
            });
        }
    }

    outList.push({
        name: 'Illustration',
        value: resp.reportUrl,
        type: 'string',
    });

    return outList;
};

export const calcfocusAPI = async (
    formData: ProsperFormValues,
    parameterStore: ParameterStore,
    contactFormStore: ContactFormStore | null = null,
) => {
    const {approval, prequal} = parameterStore;

    const year = approval.byear.value;
    const month = approval.bmonth.value;
    const day = approval.bday.value;

    const isoDOB = formatDate(year, month, day);

    const request = {
        applicationId: parameterStore.instantIulApplicationId as string,
        firstName: approval.firstName.value,
        lastName: approval.lastName.value,
        phoneNumber: approval.phoneNumber.value,
        dob: isoDOB,
        age: prequal.age.value,
        gender: approval.gender.value || prequal.gender.value,
        address: approval.address.value,
        state: approval.state.value,
        zip: approval.addressZipCode.value,
        city: approval.addressCity.value,
        ...formData,
    };

    if (contactFormStore) {
        parameterStore.prosperRequest = request as any;
        contactFormStore.saveForm();
    }

    const resp = await fetchPolicyData(request);

    const policySummary = resp?.policyInfo?.policySummary?.[0];
    if (!policySummary) {
        return {error: 'There was an issue fetching the policy'};
    }

    const outList = generateOutList(formData, policySummary, parameterStore, resp);
    return {
        faceAmount: policySummary?.coverageSummary?.[0]?.faceAmount ?? 'No face amount found',
        monthlyPremium: policySummary?.scenarioSummary?.[0]?.endOfYearValues?.[1]?.sumPremiumsPaid / 12,
        lapseAge: last(policySummary?.scenarioSummary?.[0]?.endOfYearValues as any[])?.attainedAge,
        quoteAge: first(policySummary?.scenarioSummary?.[0]?.endOfYearValues as any[])?.attainedAge - 1,
        productName: 'PROSPER',
        req: resp,
        changes: resp.changes,
        out: outList,
    };
};

function getAllProjectionAges(insuredAge: number): number[] {
    const allAges = [65, 70, 75, 80, 85];
    return allAges.filter((age) => age > insuredAge);
}

function getCashValueAtAge(parameterStore: ParameterStore, policySummary: any) {
    const {approval} = parameterStore;
    const bday = approval.bday.value as number;
    const bmonth = approval.bmonth.value as number;
    const byear = approval.byear.value as number;

    let birthDate = moment([byear, bmonth - 1, bday]);
    const age = moment().diff(birthDate, 'years');

    const projectionAges = getAllProjectionAges(age);
    const results: Array<[string, number]> = [];

    const scenarioSummary = policySummary.scenarioSummary[0];

    for (const projectionAge of projectionAges) {
        const endOfYearValue = scenarioSummary.endOfYearValues.find((v: any) => v.attainedAge === projectionAge);
        const surrenderValue = endOfYearValue?.surrenderCashValue;
        if (surrenderValue != null) {
            results.push([`Cash Value at Age ${projectionAge}`, surrenderValue]);
        }
    }
    return results.length > 0 ? results : null;
}

function getSumCumulativePremiumsPaidAtAge(parameterStore: ParameterStore, policySummary: any) {
    const {approval} = parameterStore;
    const bday = approval.bday.value as number;
    const bmonth = approval.bmonth.value as number;
    const byear = approval.byear.value as number;

    let birthDate = moment([byear, bmonth - 1, bday]);
    const age = moment().diff(birthDate, 'years');

    const projectionAges = getAllProjectionAges(age);
    const scenarioSummary = policySummary.scenarioSummary[0];

    const results: Array<[string, number]> = [];

    for (const projectionAge of projectionAges) {
        const endOfYearValue = scenarioSummary.endOfYearValues.find((v: any) => v.attainedAge === projectionAge);
        const premiumValue = endOfYearValue?.sumCumulativePremiumsPaid;
        if (premiumValue != null) {
            results.push([`Total Contribution at age ${projectionAge}`, premiumValue]);
        }
    }

    return results.length > 0 ? results : null;
}
